import { isEqual } from "lodash";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const q = ref<string | undefined>(undefined);
const sessionId = ref<string | undefined>(undefined);
const homeInstitutionId = ref<string | undefined>(undefined);
const statusId = ref<number | undefined>(undefined);
const channelId = ref<string | undefined>(undefined);
const cityId = ref<number | undefined>(undefined);
// Per-column filters added alongside the unified q search (see app/module/custom_pricing/schema.py).
const titleQ = ref<string | undefined>(undefined);
const sessionNameQ = ref<string | undefined>(undefined);
const overrideId = ref<string | undefined>(undefined);
const homeInstitutionNameQ = ref<string | undefined>(undefined);
const updatedByQ = ref<string | undefined>(undefined);

export const useFilters = () => {
  const route = useRoute();
  const router = useRouter();

  watch(
    () => route.query,
    (newParams, oldParams) => {
      if (newParams === oldParams) {
        return;
      }

      const newQ = Array.isArray(newParams.q) ? (newParams.q?.[0] ?? undefined) : (newParams.q ?? undefined);
      if (!isEqual(newQ, q.value)) {
        q.value = newQ;
      }

      const newSessionId = Array.isArray(newParams.sessionId)
        ? (newParams.sessionId?.[0] ?? undefined)
        : (newParams.sessionId ?? undefined);
      if (!isEqual(newSessionId, sessionId.value)) {
        sessionId.value = newSessionId;
      }

      const newHomeInstitutionId = Array.isArray(newParams.homeInstitutionId)
        ? (newParams.homeInstitutionId?.[0] ?? undefined)
        : (newParams.homeInstitutionId ?? undefined);
      if (!isEqual(newHomeInstitutionId, homeInstitutionId.value)) {
        homeInstitutionId.value = newHomeInstitutionId;
      }

      const rawStatusId = Array.isArray(newParams.statusId)
        ? (newParams.statusId?.[0] ?? undefined)
        : (newParams.statusId ?? undefined);
      const newStatusId = rawStatusId !== undefined ? Number(rawStatusId) : undefined;
      if (!isEqual(newStatusId, statusId.value)) {
        statusId.value = Number.isFinite(newStatusId) ? newStatusId : undefined;
      }

      const newChannelId = Array.isArray(newParams.channelId)
        ? (newParams.channelId?.[0] ?? undefined)
        : (newParams.channelId ?? undefined);
      if (!isEqual(newChannelId, channelId.value)) {
        channelId.value = newChannelId;
      }

      const rawCityId = Array.isArray(newParams.cityId)
        ? (newParams.cityId?.[0] ?? undefined)
        : (newParams.cityId ?? undefined);
      const newCityId = rawCityId !== undefined ? Number(rawCityId) : undefined;
      if (!isEqual(newCityId, cityId.value)) {
        cityId.value = Number.isFinite(newCityId) ? newCityId : undefined;
      }

      const newTitleQ = Array.isArray(newParams.titleQ)
        ? (newParams.titleQ?.[0] ?? undefined)
        : (newParams.titleQ ?? undefined);
      if (!isEqual(newTitleQ, titleQ.value)) {
        titleQ.value = newTitleQ;
      }

      const newSessionNameQ = Array.isArray(newParams.sessionNameQ)
        ? (newParams.sessionNameQ?.[0] ?? undefined)
        : (newParams.sessionNameQ ?? undefined);
      if (!isEqual(newSessionNameQ, sessionNameQ.value)) {
        sessionNameQ.value = newSessionNameQ;
      }

      const newOverrideId = Array.isArray(newParams.overrideId)
        ? (newParams.overrideId?.[0] ?? undefined)
        : (newParams.overrideId ?? undefined);
      if (!isEqual(newOverrideId, overrideId.value)) {
        overrideId.value = newOverrideId;
      }

      const newHomeInstitutionNameQ = Array.isArray(newParams.homeInstitutionNameQ)
        ? (newParams.homeInstitutionNameQ?.[0] ?? undefined)
        : (newParams.homeInstitutionNameQ ?? undefined);
      if (!isEqual(newHomeInstitutionNameQ, homeInstitutionNameQ.value)) {
        homeInstitutionNameQ.value = newHomeInstitutionNameQ;
      }

      const newUpdatedByQ = Array.isArray(newParams.updatedByQ)
        ? (newParams.updatedByQ?.[0] ?? undefined)
        : (newParams.updatedByQ ?? undefined);
      if (!isEqual(newUpdatedByQ, updatedByQ.value)) {
        updatedByQ.value = newUpdatedByQ;
      }
    },
    { immediate: true },
  );

  const updateQ = async (value?: string): Promise<void> => {
    q.value = value;
    await router.push({ query: { ...route.query, q: value } });
  };

  const updateSessionId = async (value?: string): Promise<void> => {
    sessionId.value = value;
    await router.push({ query: { ...route.query, sessionId: value } });
  };

  const updateHomeInstitutionId = async (value?: string): Promise<void> => {
    homeInstitutionId.value = value;
    await router.push({ query: { ...route.query, homeInstitutionId: value } });
  };

  const updateStatusId = async (value?: number): Promise<void> => {
    statusId.value = value;
    await router.push({ query: { ...route.query, statusId: value !== undefined ? `${value}` : undefined } });
  };

  const updateChannelId = async (value?: string): Promise<void> => {
    channelId.value = value;
    await router.push({ query: { ...route.query, channelId: value } });
  };

  const updateCityId = async (value?: number): Promise<void> => {
    cityId.value = value;
    await router.push({ query: { ...route.query, cityId: value !== undefined ? `${value}` : undefined } });
  };

  const updateTitleQ = async (value?: string): Promise<void> => {
    titleQ.value = value;
    await router.push({ query: { ...route.query, titleQ: value } });
  };

  const updateSessionNameQ = async (value?: string): Promise<void> => {
    sessionNameQ.value = value;
    await router.push({ query: { ...route.query, sessionNameQ: value } });
  };

  const updateOverrideId = async (value?: string): Promise<void> => {
    overrideId.value = value;
    await router.push({ query: { ...route.query, overrideId: value } });
  };

  const updateHomeInstitutionNameQ = async (value?: string): Promise<void> => {
    homeInstitutionNameQ.value = value;
    await router.push({ query: { ...route.query, homeInstitutionNameQ: value } });
  };

  const updateUpdatedByQ = async (value?: string): Promise<void> => {
    updatedByQ.value = value;
    await router.push({ query: { ...route.query, updatedByQ: value } });
  };

  const clearFilters = async () => {
    await router.push({
      query: {},
    });
  };

  return {
    q,
    updateQ,
    sessionId,
    updateSessionId,
    homeInstitutionId,
    updateHomeInstitutionId,
    statusId,
    updateStatusId,
    channelId,
    updateChannelId,
    cityId,
    updateCityId,
    titleQ,
    updateTitleQ,
    sessionNameQ,
    updateSessionNameQ,
    overrideId,
    updateOverrideId,
    homeInstitutionNameQ,
    updateHomeInstitutionNameQ,
    updatedByQ,
    updateUpdatedByQ,
    clearFilters,
  };
};
